import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { Observable, Subject, from } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AutocompleteComponent } from '@plugins/client/forms/autocomplete/autocomplete.component';
import {
  FormControlComponent,
  setControlRelationship,
} from '@plugins/client/forms/form';
import { resizeObservable } from '@plugins/client/utils';
import { Maybe, SingleOrArray } from '@plugins/shared/types/helpers';
import {
  GroupPublic,
  IdentityPublic,
  UserPublic,
} from '@plugins/userdetails-lib/entity';

import {
  BASE_LINE_HEIGHT_SIZE,
  PICKED_HEIGHT_TO_NAME_RATIO,
  TRANSLATION_KEYS,
} from '../../private/config';
import { BaseCVAHandler, SearchTypes } from '../../private/utils/types.private';
import { generateCompositeSize } from '../../private/utils/utils';
import { PickedEntitiesService } from '../services';
import { UserDetailsService } from '../services/user-details.service';

const { SEARCH } = TRANSLATION_KEYS;

type OnChange = (arg: unknown) => void;

/** Search for user or group entities depending on the allowed search type. Implements
 * `ControlValueAccessor` and expects a `FormControl<string>` search control.
 *
 * Example:
 ```html
  <!-- Search type defaults to 'all'-->
  <plugins-user-search />


<plugins-user-search
  searchType="user"
  [placeholderText]="'Custom placeholder text'"
  [formControl]="searchControl"
  color="colorValue" />
  ```
 */
@Component({
  selector: 'plugins-user-search',
  standalone: false,
  templateUrl: './user-search.component.html',
  styleUrl: './user-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchComponent
  extends FormControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  readonly autocompleteRef = viewChild<AutocompleteComponent<IdentityPublic>>(
    AutocompleteComponent,
  );

  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   * Defaults to `all`. */
  readonly searchType = input<SearchTypes>('all');

  /** Input for overriding original placeholder text in the searchbar. */
  readonly placeholderText = input<Maybe<string>>();
  /** Controls the size of the returned search results.  */
  readonly size = input<Maybe<string | number>>('');
  /** Should the returned results have a detailed title associated with them? */
  readonly detailedTitle = input<Maybe<boolean>>();
  /** ID of the error message element associated with the input. */
  readonly errorId = input<string>();

  /** Limit responses of the user API to members of specified groups.
   * This has no effect if the `searchType` is set to `group` */
  readonly includedGroups = input<SingleOrArray<string>>([]);
  /** Outline color for the input and options on keyboard navigation */
  readonly color = input<Maybe<string>>('');

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  destroy$ = new Subject<void>();
  protected readonly usersRepository = signal<IdentityPublic[]>([]);

  readonly lineHeightSize = computed(() =>
    this.size()
      ? generateCompositeSize(this.size(), PICKED_HEIGHT_TO_NAME_RATIO)
      : BASE_LINE_HEIGHT_SIZE,
  );

  readonly placeholder = computed(() => ({
    custom: this.placeholderText(),
    default: SEARCH[this.searchType()],
  }));

  // ControlValueAccessor properties
  private onChange!: OnChange;
  private onTouched!: BaseCVAHandler;

  protected cdr = inject(ChangeDetectorRef);
  protected userDetailsService = inject(UserDetailsService);
  protected pickedService = inject(PickedEntitiesService);
  protected elRef = inject(ElementRef) as ElementRef<HTMLElement>;

  constructor() {
    super();
    if (this.controlDirective) {
      this.controlDirective.valueAccessor = this;
    }
  }

  /** Since available entities will be rendered as children of the
   * `body` element, the `fontSize: inherit` chain is broken.
   *
   * That is why we listen for height changes on the parent element. */
  fontSize$: Observable<string> | undefined;

  override ngOnInit(): void {
    super.ngOnInit();
    this.fontSize$ = resizeObservable(this.elRef.nativeElement).pipe(
      debounceTime(10),
      map(() => getComputedStyle(this.elRef.nativeElement).fontSize),
      distinctUntilChanged(),
      startWith(getComputedStyle(this.elRef.nativeElement).fontSize),
      tap(() => {
        // Detect changes needed because of ng-template option elements
        this.cdr.detectChanges();
      }),
    );
    if (this.controlDirective) {
      this.setSearchControl();
      this.searchControl.valueChanges
        .pipe(
          switchMap((val) => {
            return from(
              this.userDetailsService.searchEntities(val, {
                pickedEntities: this.pickedService.pickedIds,
                excludedEntities: this.pickedService.excludedIds,
                type: this.searchType(),
                includedGroups: this.includedGroups(),
              }),
            );
          }),
          tap((users) => {
            this.usersRepository.set(users);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();
    }
  }

  onSelect(event: UserPublic | GroupPublic) {
    this.searchControl.setValue('');
    this.pickedService.addIds(event.id);
  }

  handleBlur(): void {
    if (this.controlDirective?.control?.pristine) {
      this.controlDirective.control.markAsTouched();
      this.controlDirective.control.updateValueAndValidity();
    }
  }

  private setSearchControl(): void {
    setControlRelationship({
      controlDirective: this.controlDirective,
      formControl: this.searchControl,
      destroy$: this.destroy$,
      onChange: this.onChange,
      emitEvent: true,
      changeDetectorRef: this.cdr,
      writeValueToParent: true,
      inheritValidators: true,
    });
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    this.searchControl.patchValue(value);
  }
  registerOnChange(fn: OnChange): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: BaseCVAHandler): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }
    this.searchControl.enable({ emitEvent: false });
  }

  override focus() {
    this.autocompleteRef()?.focus();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
